import React from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Download,
  FilterList,
} from "@mui/icons-material";

import {
  getSurveyNotificationLogs
} from "../../../../services/choices/choices_services";
import { useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/main/main_layout";
import NotificationFilterDialog from "./survey_notification_filter_dialog";

const STATUS_LABELS: Record<string, string> = {
  USER_NOT_FOUND: "User Not Found",
  DEVICE_ID_MISSING: "Device ID Missing",
  FCM_TOKEN_MISSING: "FCM Token Missing",
  PUSH_FAILED: "Push Failed",
  SUCCESS: "Success",
  PERMANENT_FAILURE: "Permanent Failure",
};

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    color:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  }
> = {
  USER_NOT_FOUND: {
    label: "User Not Found",
    color: "warning",
  },

  DEVICE_ID_MISSING: {
    label: "Device ID Missing",
    color: "warning",
  },

  FCM_TOKEN_MISSING: {
    label: "Retry - FCM Token Missing",
    color: "warning",
  },

  PUSH_FAILED: {
    label: "Retry - Push Failed",
    color: "error",
  },

  SUCCESS: {
    label: "Success",
    color: "success",
  },

  PERMANENT_FAILURE: {
    label: "Permanently Failed",
    color: "error",
  },
};

const SurveyNotificationLogs = () => {
  const { eventId } = useParams<{ eventId: string }>();

  const [page, setPage] = React.useState(1);

  const [openFilter, setOpenFilter] = React.useState(false);
  const [downloadLoading, setDownloadLoading] =
    React.useState(false);

  const [filters, setFilters] = React.useState<any>({
    dateFrom: "",
    dateTo: "",
    status: "",
    sortOrder: "DESC",
  });

  const { data, isLoading, isFetching, isError } = useQuery(
    ["notification-logs", eventId, page, filters, downloadLoading],
    async () => {
      const response = await getSurveyNotificationLogs({
        page,
        size: 10,
        eventId,
        ...filters,
      });

      return response?.data?.data?.data || {};
    },
    {
      keepPreviousData: true,
    }
  );

  const list = data?.list || [];
  const totalCount = data?.total || 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleDownloadLogs = async () => {
    try {
      setDownloadLoading(true);

      const response = await getSurveyNotificationLogs({
        page,
        size: 10,
        eventId,
        download: true,
        ...filters,
      });

      const blob = new Blob([response.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "notification-logs-file.xlsx";

      document.body.appendChild(a);
      a.click();

      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    setOpenFilter(false);
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      status: "",
      sortOrder: "DESC",
    });
    setPage(1);
  };

  return (
    <MainLayout>
      <Box p={3}>
        {/* HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >

          <Typography variant="h5" fontWeight={700}>
            <IconButton onClick={() => {
              window.history.back();
            }}>
              <ArrowBack />
            </IconButton>
            Notification Logs
          </Typography>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setOpenFilter(true)}
            >
              Filters
            </Button>

            <Button
              variant="contained"
              startIcon={
                !downloadLoading ? <Download /> : undefined
              }
              disabled={downloadLoading}
              onClick={async () => {
                try {
                  setDownloadLoading(true);
                  await handleDownloadLogs();
                } finally {
                  setDownloadLoading(false);
                }
              }}
            >
              {downloadLoading ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                />
              ) : (
                "Download"
              )}
            </Button>
          </Box>
        </Box>

        {/* TABLE */}
        {isLoading || isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography color="error">
            Failed to load notification logs.
          </Typography>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 3 }}>
            <TableContainer sx={{ maxHeight: 650 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "S.No. ",
                      "Survey Name",
                      "Survey ID",
                      "User ID",
                      "Date",
                      "Status",
                    ].map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          fontWeight: 700,
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {list.length > 0 ? (
                    list.map((item: any,index:number) => (
                      <TableRow key={item.userId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {index+1}.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.surveyName}
                        </TableCell>
                        <TableCell>{item.surveyId}</TableCell>
                        <TableCell>{item.userId}</TableCell>
                        <TableCell>
                          {new Date(item.date).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              STATUS_CONFIG[item.status]?.label ||
                              item.status ||
                              "Unknown"
                            }
                            color={
                              STATUS_CONFIG[item.status]?.color ||
                              "default"
                            }
                            size="small"
                            sx={{ fontWeight: 600, minWidth: 160 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
              <TablePagination
                component="div"
                count={totalCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={10}
                rowsPerPageOptions={[]}
              />
            </Box>
          </Paper>
        )}

        {/* FILTER MODAL */}
        <NotificationFilterDialog
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      </Box>
    </MainLayout>
  );
};

export default SurveyNotificationLogs;